import * as React from 'react'
import { useRouter } from 'next/router'

import { Routes, useSiteConfig } from '@thg-commerce/enterprise-core'
import { BasketIcon as Basket } from '@thg-commerce/gravity-icons/src'
import { CombinedThemeInterface } from '@thg-commerce/gravity-patterns'
import {
  keyframes,
  styled,
  withTheme,
} from '@thg-commerce/gravity-patterns/theme'
import { css, mq, Text } from '@thg-commerce/gravity-theme'

export interface BasketIconProps {
  totalQuantity?: number
  altText?: string
  theme: CombinedThemeInterface
}

const bounceAnimation = keyframes`
  0%, 84%, 100% {
    transform: translate3d(0, 0, 0);
  }
  85% {
    transform: translate3d(0, -16px, 0);
  }
  87% {
    transform: translate3d(0, 4px, 0);
  }
  89% {
    transform: translate3d(0, -10px, 0);
  }
  91% {
    transform: translate3d(0, 3px, 0);
  }
  93% {
    transform: translate3d(0, -6px, 0);
  }
  95% {
    transform: translate3d(0, 2px, 0);
  }
  97% {
    transform: translate3d(0, -3px, 0);
  }
  99% {
    transform: translate3d(0, 1px, 0);
  }
`

const IconSvg = styled.svg`
  width: ${(props) =>
    props.theme.patterns.header.dropdowns.basket.mobileIcon.width};
  height: ${(props) =>
    props.theme.patterns.header.dropdowns.basket.mobileIcon.height};

  ${(props) => mq(props.theme.breakpointUtils.map, 'lg')} {
    width: ${(props) =>
      props.theme.patterns.header.dropdowns.basket.icon.width};
    height: ${(props) =>
      props.theme.patterns.header.dropdowns.basket.icon.height};
  }
`
const isAnimationEnabled = (props) => {
  const animationEnabledOnBasket =
    props.enableBounceAnimationBasket === true && props.isBasketPage

  return (
    (props.theme?.patterns?.header?.dropdowns?.basket?.quantityStyling
      ?.enableBounceAnimation === true &&
      !props.isBasketPage) ||
    animationEnabledOnBasket
  )
}

const getBackgroundColor = (props, quantityStyling, animationEnabled) => {
  return (animationEnabled || props.isBasketPage) && props.hasItems
    ? quantityStyling?.populatedBackgroundColor ||
        quantityStyling?.backgroundColor
    : quantityStyling?.emptyBackgroundColor || quantityStyling?.backgroundColor
}

const NumberCircle = styled.div<{
  hasItems: boolean
  isBasketPage: boolean
  enableBounceAnimationBasket?: boolean
}>`
  ${(props) =>
    Text(
      props.theme.patterns.header.dropdowns.basket.quantityStyling.mobileText
        .entry,
      props.theme.patterns.header.dropdowns.basket.quantityStyling.mobileText
        .style,
    )};

  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: -5px;
  right: -5px;
  border-radius: 100%;

  ${(props) => {
    const quantityStyling =
      props.theme.patterns.header.dropdowns.basket.quantityStyling

    const animationEnabled = isAnimationEnabled(props)
    const backgroundColor = getBackgroundColor(
      props,
      quantityStyling,
      animationEnabled,
    )

    return css`
      height: ${quantityStyling.mobileHeight};
      background-color: ${backgroundColor};
      color: ${quantityStyling.text.textColor};
      text-decoration: ${quantityStyling.text.textDecoration};
      text-transform: ${quantityStyling.text.transform};
      min-width: ${quantityStyling.mobileWidth};
      top: ${quantityStyling.mobilePosition.top};
      left: ${quantityStyling.mobilePosition.left};
      right: ${quantityStyling.mobilePosition.right};
      height: ${quantityStyling.mobileHeight};
      width: ${quantityStyling.mobileWidth};

      ${props.hasItems &&
      animationEnabled &&
      css`
        animation: ${bounceAnimation} 5s ease-out infinite;
      `}
    `
  }}

  ${(props) => mq(props.theme.breakpointUtils.map, 'lg')} {
    ${(props) =>
      Text(
        props.theme.patterns.header.dropdowns.basket.quantityStyling.text.entry,
        props.theme.patterns.header.dropdowns.basket.quantityStyling.text.style,
      )}

    ${(props) => {
      const quantityStyling =
        props.theme.patterns.header.dropdowns.basket.quantityStyling

      const animationEnabled = isAnimationEnabled(props)
      const backgroundColor = getBackgroundColor(
        props,
        quantityStyling,
        animationEnabled,
      )

      return css`
        top: ${quantityStyling.position.top};
        right: ${quantityStyling.position.right};
        left: ${quantityStyling.position.left};
        color: ${quantityStyling.text.textColor};
        height: ${quantityStyling.height};
        width: ${quantityStyling.width};
        background-color: ${backgroundColor};
      `
    }}

    &:hover {
      ${(props) =>
        Text(
          props.theme.patterns.header.dropdowns.basket.quantityStyling.hoverText
            .entry,
          props.theme.patterns.header.dropdowns.basket.quantityStyling.hoverText
            .style,
        )};

      ${(props) => {
        const quantityStyling =
          props.theme.patterns.header.dropdowns.basket.quantityStyling

        const animationEnabled = isAnimationEnabled(props)
        const backgroundColor = getBackgroundColor(
          props,
          quantityStyling,
          animationEnabled,
        )

        return css`
          background-color: ${backgroundColor};
          color: ${quantityStyling.hoverText.textColor};
          text-decoration: ${quantityStyling.hoverText.textDecoration};
          text-transform: ${quantityStyling.hoverText.transform};
        `
      }}
    }
  }
`

const IconContainer = styled.div`
  display: flex;
  position: relative;
`

export const BasketIcon = withTheme((props: BasketIconProps) => {
  const { enableBounceAnimationBasket } = useSiteConfig()
  const router = useRouter()
  const isBasketPage = router.asPath === Routes.Basket

  const totalBasketQuantity =
    props.totalQuantity && props.totalQuantity ? props.totalQuantity : 0

  const hasItems = totalBasketQuantity > 0

  return (
    <IconContainer>
      {props.theme.patterns.header.dropdowns.basket.icon.svgPath !== '' ? (
        <IconSvg
          data-name="Layer 1"
          xmlns="http://www.w3.org/2000/svg"
          viewBox={props.theme.patterns.header.dropdowns.basket.icon.viewBox}
        >
          <path
            d={props.theme.patterns.header.dropdowns.basket.icon.svgPath}
            fill-rule="evenodd"
          ></path>
        </IconSvg>
      ) : (
        <Basket />
      )}
      <NumberCircle
        data-testid="basket-item-count"
        hasItems={hasItems}
        isBasketPage={isBasketPage}
        enableBounceAnimationBasket={enableBounceAnimationBasket}
      >
        {totalBasketQuantity}
      </NumberCircle>
    </IconContainer>
  )
})
